// Edit an event (organizer / creator / club admin — RLS enforces it
// server-side, the UI checks the loaded rows to fail fast with a friendly
// message). Field edits go through one updateEvent patch; route-link changes
// are diffed against the loaded rows and applied as create/update/delete
// mutations keyed on the (event_id, route_id) natural key.
import { useState, type FormEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { X } from "lucide-react";
import { gqlRequest } from "../lib/gql";
import {
  activitiesToWire,
  CREATE_EVENT_ROUTE,
  DELETE_EVENT_ROUTE,
  EVENT_DETAIL,
  MY_CLUB_MEMBERSHIPS,
  UPDATE_EVENT,
  UPDATE_EVENT_ROUTE,
  type EventDetailResult,
  type MyClubMembershipsResult,
  type UpdateEventResult,
} from "../lib/queries";
import { useAuth } from "../lib/auth";
import {
  ACTIVITIES,
  ACTIVITY_ICONS,
  EVENT_JOIN_POLICIES,
  EVENT_RECURRENCES,
  EVENT_VISIBILITIES,
  type Activity,
  type EventJoinPolicy,
  type EventRecurrence,
  type EventVisibility,
} from "../lib/format";
import { useT } from "../lib/i18n";
import { LoginGate } from "../components/LoginGate";
import { PlacePicker, type PickedPlace } from "../components/PlacePicker";
import { RouteLinkPicker, type RouteLink } from "../components/RouteLinkPicker";
import {
  Chip,
  EmptyState,
  ErrorBox,
  Field,
  Select,
  Spinner,
  TextArea,
  TextInput,
} from "../components/ui";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { pointCoords } from "../lib/geo";

export function EditEventPage({ id }: { id: string }) {
  return (
    <LoginGate action="gateAction.editEvent" next={`/event/${id}/edit`}>
      <EditEventLoader id={id} />
    </LoginGate>
  );
}

type Translate = ReturnType<typeof useT>["t"];

function friendlyError(err: unknown, t: Translate): string {
  const msg = err instanceof Error ? err.message : String(err);
  // RLS filtering the row out surfaces as "no row matched" from pdbq.
  if (/no row matched|permission|denied|policy|violates row-level/i.test(msg)) return t("editEvent.noPermission");
  if (/violates|constraint|invalid input/i.test(msg)) return t("err.rejected", { msg });
  return msg;
}

/** timestamptz ISO → datetime-local input value in the viewer's timezone. */
function toLocalInput(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function EditEventLoader({ id }: { id: string }) {
  const { personId } = useAuth();
  const { t } = useT();

  const { data, isLoading, error } = useQuery({
    queryKey: ["event", id],
    queryFn: () => gqlRequest<EventDetailResult>(EVENT_DETAIL, { id }),
  });

  const memberships = useQuery({
    queryKey: ["my-club-memberships", personId],
    enabled: !!personId,
    queryFn: () => gqlRequest<MyClubMembershipsResult>(MY_CLUB_MEMBERSHIPS, { personId }),
  });

  if (isLoading || memberships.isLoading) return <Spinner />;
  if (error) return <ErrorBox error={error} />;
  const event = data?.event;
  if (!event) return <EmptyState>{t("event.notFound")}</EmptyState>;

  const myRow = event.eventParticipantsByEventId.nodes.find((p) => p.personId === personId);
  const clubRow = event.clubId
    ? memberships.data?.clubMembers.nodes.find((m) => m.clubId === event.clubId)
    : null;
  const canManage =
    event.createdBy === personId ||
    (myRow?.role === "ORGANIZER" && myRow.status === "CONFIRMED") ||
    (clubRow?.status === "ACTIVE" && (clubRow.role === "OWNER" || clubRow.role === "ADMIN"));
  if (!canManage) return <EmptyState>{t("editEvent.notOrganizer")}</EmptyState>;

  return <EditEventForm event={event} />;
}

function EditEventForm({ event }: { event: NonNullable<EventDetailResult["event"]> }) {
  const { personId } = useAuth();
  const { t, labelizeT } = useT();
  const [, navigate] = useLocation();
  const queryClient = useQueryClient();

  const memberships = useQuery({
    queryKey: ["my-club-memberships", personId],
    enabled: !!personId,
    queryFn: () => gqlRequest<MyClubMembershipsResult>(MY_CLUB_MEMBERSHIPS, { personId }),
  });
  const myClubs = (memberships.data?.clubMembers.nodes ?? []).filter(
    (m) => m.status === "ACTIVE" && m.club != null,
  );

  // Already ordered by sort server-side (EVENT_DETAIL orders the connection).
  const initialRouteLinks: RouteLink[] = event.eventRoutesByEventId.nodes.map((n) => ({
    route: { id: n.route.id, name: n.route.name, activities: n.route.activities, durationMin: n.route.durationMin },
    style: "",
    result: "",
  }));

  const [title, setTitle] = useState(event.title);
  const [description, setDescription] = useState(event.description ?? "");
  const [activities, setActivities] = useState<Activity[]>(event.activities ?? []);
  const [startsAt, setStartsAt] = useState(toLocalInput(event.startsAt));
  const [endsAt, setEndsAt] = useState(toLocalInput(event.endsAt));
  const [joinPolicy, setJoinPolicy] = useState<EventJoinPolicy>(event.joinPolicy);
  const [visibility, setVisibility] = useState<EventVisibility>(event.visibility);
  const [recurrence, setRecurrence] = useState<EventRecurrence>(event.recurrence);
  const [recurrenceUntil, setRecurrenceUntil] = useState(event.recurrenceUntil ?? "");
  const [maxParticipants, setMaxParticipants] = useState(event.maxParticipants != null ? String(event.maxParticipants) : "");
  const [clubId, setClubId] = useState(event.clubId ?? "");
  const [coords, setCoords] = useState<[number, number] | null>(pointCoords(event.location));
  const [place, setPlace] = useState<PickedPlace | null>(
    event.place ? { id: event.place.id, name: event.place.name, type: event.place.type, elevation: null } : null,
  );
  const [showOnMap, setShowOnMap] = useState(event.showOnMap);
  const [routeLinks, setRouteLinks] = useState<RouteLink[]>(initialRouteLinks);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  // The event's current club may not be among the editor's own memberships
  // (a club admin is, but a creator who since left isn't) — keep it offered.
  const clubOptions = [
    ...(event.clubId && !myClubs.some((m) => m.clubId === event.clubId)
      ? [{ clubId: event.clubId, name: event.club?.name ?? t("common.unnamed") }]
      : []),
    ...myClubs.map((m) => ({ clubId: m.clubId, name: m.club!.name })),
  ];

  function onClubChange(next: string) {
    setClubId(next);
    if (!next) {
      if (visibility === "MEMBERS") setVisibility("PUBLIC");
      if (joinPolicy === "MEMBERS_ONLY") setJoinPolicy("OPEN");
    }
  }
  const visibilityOptions = EVENT_VISIBILITIES.filter((v) => clubId || v !== "MEMBERS");
  const joinPolicyOptions = EVENT_JOIN_POLICIES.filter((p) => clubId || p !== "MEMBERS_ONLY");

  const save = useMutation({
    mutationFn: async () => {
      const patch = {
        title: title.trim(),
        description: description.trim() || null,
        activities: activities.length ? activitiesToWire(activities) : null,
        clubId: clubId || null,
        placeId: place?.id ?? null,
        location: coords ? { type: "Point", coordinates: coords } : null,
        startsAt: new Date(startsAt).toISOString(),
        endsAt: endsAt ? new Date(endsAt).toISOString() : null,
        joinPolicy,
        visibility,
        recurrence,
        recurrenceUntil: recurrence !== "NONE" && recurrenceUntil ? recurrenceUntil : null,
        maxParticipants: maxParticipants ? Math.round(Number(maxParticipants)) : null,
        showOnMap,
      };
      const result = await gqlRequest<UpdateEventResult>(UPDATE_EVENT, { id: event.id, patch });
      if (!result.updateEvent?.event) throw new Error(t("editEvent.noPermission"));

      // Route links diffed by route id against the loaded rows: delete
      // removed, create added, patch changed sort — all keyed on the
      // (event_id, route_id) natural key.
      const linkBefore = new Map(event.eventRoutesByEventId.nodes.map((n) => [n.route.id, n]));
      const linkAfter = new Set(routeLinks.map((l) => l.route.id));
      for (const routeId of linkBefore.keys()) {
        if (!linkAfter.has(routeId)) await gqlRequest(DELETE_EVENT_ROUTE, { eventId: event.id, routeId });
      }
      for (const [sort, link] of routeLinks.entries()) {
        const prev = linkBefore.get(link.route.id);
        if (!prev) {
          await gqlRequest(CREATE_EVENT_ROUTE, { eventId: event.id, routeId: link.route.id, sort });
        } else if (prev.sort !== sort) {
          await gqlRequest(UPDATE_EVENT_ROUTE, { eventId: event.id, routeId: link.route.id, sort });
        }
      }
    },
    // onSettled, not onSuccess: the patch may have landed even when a later
    // route mutation failed, so the cache must refetch either way.
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["event", event.id] });
      const clubIds = [event.clubId, clubId || null].filter((c) => c != null);
      for (const c of clubIds) {
        queryClient.invalidateQueries({ queryKey: ["club", c] });
        queryClient.invalidateQueries({ queryKey: ["club-events", c] });
      }
    },
    onSuccess: () => {
      navigate(`/event/${event.id}`);
    },
  });

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    const errors: Record<string, string> = {};
    if (!title.trim()) errors.title = t("newEvent.errTitle");
    if (!startsAt) errors.startsAt = t("newEvent.errStart");
    if (endsAt && startsAt && new Date(endsAt) <= new Date(startsAt)) errors.endsAt = t("newEvent.errEnd");
    if (maxParticipants && (!Number.isFinite(Number(maxParticipants)) || Number(maxParticipants) <= 0))
      errors.maxParticipants = t("newEvent.errCap");
    setFieldErrors(errors);
    if (Object.keys(errors).length === 0) save.mutate();
  }

  return (
    <div className="mx-auto max-w-xl p-4">
      <h1 className="mb-5 text-2xl font-semibold tracking-tight text-foreground">{t("editEvent.title")}</h1>
      <form onSubmit={onSubmit} className="space-y-4">
        <Field label={t("form.title")} error={fieldErrors.title}>
          <TextInput value={title} onChange={(e) => setTitle(e.target.value)} />
        </Field>

        <Field label={t("form.description")}>
          <TextArea rows={4} value={description} onChange={(e) => setDescription(e.target.value)} />
        </Field>

        <Field label={t("form.activities")}>
          <div className="flex flex-wrap gap-2">
            {ACTIVITIES.map((a) => {
              const Icon = ACTIVITY_ICONS[a];
              const active = activities.includes(a);
              return (
                <Chip
                  key={a}
                  active={active}
                  onClick={() => setActivities(active ? activities.filter((x) => x !== a) : [...activities, a])}
                >
                  <span className="inline-flex items-center gap-1.5">
                    <Icon aria-hidden className="size-3.5" />
                    {labelizeT(a)}
                  </span>
                </Chip>
              );
            })}
          </div>
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field label={t("newEvent.start")} error={fieldErrors.startsAt}>
            <TextInput type="datetime-local" value={startsAt} onChange={(e) => setStartsAt(e.target.value)} />
          </Field>
          <Field label={t("newEvent.endOptional")} error={fieldErrors.endsAt}>
            <TextInput type="datetime-local" value={endsAt} onChange={(e) => setEndsAt(e.target.value)} />
          </Field>
        </div>

        <Field label={t("newEvent.groupOptional")}>
          <Select value={clubId} onChange={(e) => onClubChange(e.target.value)}>
            <option value="">{t("newEvent.noGroup")}</option>
            {clubOptions.map((c) => (
              <option key={c.clubId} value={c.clubId}>
                {c.name}
              </option>
            ))}
          </Select>
          <p className="mt-1 text-xs text-muted-foreground">{t("newEvent.groupHint")}</p>
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field label={t("group.joinPolicy")}>
            <Select value={joinPolicy} onChange={(e) => setJoinPolicy(e.target.value as EventJoinPolicy)}>
              {joinPolicyOptions.map((p) => (
                <option key={p} value={p}>
                  {labelizeT(p)}
                </option>
              ))}
            </Select>
          </Field>
          <Field label={t("form.visibility")}>
            <Select value={visibility} onChange={(e) => setVisibility(e.target.value as EventVisibility)}>
              {visibilityOptions.map((v) => (
                <option key={v} value={v}>
                  {labelizeT(v)}
                </option>
              ))}
            </Select>
          </Field>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Field label={t("event.recurrence")}>
            <Select value={recurrence} onChange={(e) => setRecurrence(e.target.value as EventRecurrence)}>
              {EVENT_RECURRENCES.map((r) => (
                <option key={r} value={r}>
                  {labelizeT(r)}
                </option>
              ))}
            </Select>
          </Field>
          <Field label={t("newEvent.maxOptional")} error={fieldErrors.maxParticipants}>
            <TextInput
              type="number"
              inputMode="numeric"
              min={1}
              value={maxParticipants}
              onChange={(e) => setMaxParticipants(e.target.value)}
            />
          </Field>
        </div>
        {recurrence !== "NONE" && (
          <Field label={t("newEvent.repeatsUntil")}>
            <TextInput type="date" value={recurrenceUntil} onChange={(e) => setRecurrenceUntil(e.target.value)} />
          </Field>
        )}

        <Field label={t("newEvent.meetingPointOptional")}>
          <div className="space-y-2">
            {coords && (
              <div className="flex items-center justify-between rounded-xl border bg-accent px-3 py-2">
                <span className="text-sm text-foreground">
                  {t("newEvent.mapPoint", { lat: coords[1].toFixed(5), lng: coords[0].toFixed(5) })}
                </span>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => setCoords(null)}
                  className="ml-2 shrink-0 text-muted-foreground hover:text-destructive"
                  aria-label={t("newEvent.clearPointAria")}
                >
                  <X aria-hidden className="size-4" />
                </Button>
              </div>
            )}
            <PlacePicker selected={place ? [place] : []} onChange={(next) => setPlace(next[next.length - 1] ?? null)} />
          </div>
        </Field>

        <label className="flex items-center gap-2 text-sm text-foreground">
          <Checkbox checked={showOnMap} onCheckedChange={(checked) => setShowOnMap(checked === true)} />
          {t("newEvent.showOnMap")}
        </label>
        <p className="text-xs text-muted-foreground">{t("newEvent.showOnMapHint")}</p>

        <Field label={t("newEvent.routesOptional")}>
          <RouteLinkPicker simple links={routeLinks} onChange={setRouteLinks} />
        </Field>

        {save.error != null && (
          <Alert variant="destructive">
            <AlertDescription>{friendlyError(save.error, t)}</AlertDescription>
          </Alert>
        )}

        <div className="flex gap-3">
          <Button type="submit" disabled={save.isPending} className="flex-1 py-2.5">
            {save.isPending ? t("common.saving") : t("edit.submit")}
          </Button>
          <Button type="button" variant="outline" onClick={() => navigate(`/event/${event.id}`)}>
            {t("common.cancel")}
          </Button>
        </div>
      </form>
    </div>
  );
}
